import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const SHOOTER_LEADERBOARD_KEY = 'shooter_leaderboard';

interface FeatureFlagsContextValue {
  shooterLeaderboardEnabled: boolean;
  loading: boolean;
  setShooterLeaderboardEnabled: (enabled: boolean) => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined);

export function FeatureFlagsProvider({ children }: { children: React.ReactNode }) {
  const [shooterLeaderboardEnabled, setShooterLeaderboardEnabledState] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadFlags = async () => {
      const { data, error } = await supabase
        .from('app_feature_flags')
        .select('enabled')
        .eq('key', SHOOTER_LEADERBOARD_KEY)
        .maybeSingle();

      if (cancelled) return;
      if (error) {
        console.warn('Could not load feature flags; using safe defaults.', error.message);
        setShooterLeaderboardEnabledState(false);
      } else {
        setShooterLeaderboardEnabledState(data?.enabled === true);
      }
      setLoading(false);
    };

    void loadFlags();
    return () => { cancelled = true; };
  }, []);

  const setShooterLeaderboardEnabled = async (enabled: boolean) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('app_feature_flags')
      .update({ enabled, updated_at: new Date().toISOString(), updated_by: user?.id ?? null })
      .eq('key', SHOOTER_LEADERBOARD_KEY);

    if (error) throw error;
    setShooterLeaderboardEnabledState(enabled);
  };

  return (
    <FeatureFlagsContext.Provider value={{ shooterLeaderboardEnabled, loading, setShooterLeaderboardEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext);
  if (!context) throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  return context;
}
